import { Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { RouterProvider } from "react-router-dom";
import DefaultRouter from "./routers/DefaultRouter";
import { UserDto } from "./interfaces/UserInterfaces";
import AuthRouter from "./routers/AuthRouter";
import ConsentGate from "./components/ConsentGate/ConsentGate";
import Nav from "./components/Nav/Nav";
import ManagerDto from "./interfaces/ManagerDto";
import ManagerAuthRouter from "./routers/ManagerAuthRouter";
import AuthApi from "./api/AuthApi";
import WebSocketService from "./utils/WebSocketService";

// Create a context for the user. It stores a UserDto object
export const UserContext = React.createContext<UserDto | null>(null);
export const ManagerContext = React.createContext<ManagerDto | null>(null);

const App = () => {
  const [user, setUser] = React.useState<UserDto | null>(null);
  const [manager, setManager] = React.useState<ManagerDto | null>(null);
  
  useEffect(() => {
    // Try and get the user from session storage
    const userStr = window.localStorage.getItem('user');
    if(userStr != null) {
      try {
        const userObj: UserDto = JSON.parse( `${userStr}` );
        setUser(userObj);
        
        // Validate user session with /me endpoint
        AuthApi.me()
          .then((fullUser) => {
            // User is valid, update with fresh data
            console.log('User session validated');
            if (fullUser && 'error' in fullUser && fullUser.error === 'Unauthorized') {
              console.log('User session invalid, logging out:', fullUser.error);
              AuthApi.logout()
                .catch(() => {
                  console.log('Logout request failed, clearing localStorage anyway');
                })
                .finally(() => {
                  window.localStorage.removeItem('user');
                  setUser(null);
                });
            }
          })
          .catch((error) => {
            console.log('error');
            console.log(error);
          });
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
        window.localStorage.removeItem('user');
        setUser(null);
      }
    }

    const managerStr = window.localStorage.getItem('manager');
    if(managerStr != null) {
      try {
        const managerObj: ManagerDto = JSON.parse( `${managerStr}` );
        setManager(managerObj);
        
        // Validate manager session with /manager/me endpoint
        AuthApi.managerMe()
          .then((managerData) => {
            // Manager is valid, update with fresh data
            console.log('Manager session validated');
            if (managerData && 'error' in managerData && managerData.error === 'Unauthorized') {
              console.log('Manager session invalid, logging out:', managerData.error);
              AuthApi.logout()
                .catch(() => {
                  console.log('Logout request failed, clearing localStorage anyway');
                })
                .finally(() => {
                  window.localStorage.removeItem('manager');
                  setManager(null);
                });
            }
          })
          .catch((error) => {
            console.log('Manager session validation error:', error);
            // Manager session is invalid, logout and clear localStorage
            AuthApi.logout()
              .catch(() => {
                console.log('Logout request failed, clearing localStorage anyway');
              })
              .finally(() => {
                window.localStorage.removeItem('manager');
                setManager(null);
              });
          });
      } catch (e) {
        console.error("Failed to parse manager from sessionStorage", e);
        window.localStorage.removeItem('manager');
        setManager(null);
      }
    }

    // Check for ticket preference URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');

    const validTypes = ['class-c', 'cdl', 'warrant'];

    if (type && validTypes.includes(type)) {
      localStorage.setItem('preferredTicketType', type);
    }

    // Initialize WebSocket connection
    WebSocketService.connect();
    
    // Cleanup WebSocket on app unmount
    return () => {
      WebSocketService.disconnect();
    };
  }, []);

  const helmet = ()=>{
    return (
      <Helmet>
        <title>Texas Ticket Doctor</title>
        <meta name="description" content="Tx Ticket Doctor" />
        <meta property="og:title" content="Tx Ticket Doctor" />
        <meta property="og:description" content="Tx Ticket Doctor" />
        <meta property="robots" content="index" />
        <meta property="author" content="Tx Ticket Doctor" />
        <meta property="publisher" content="Tx Ticket Doctor" />
        <meta property="og:image" content="" />
      </Helmet>
    );
  }

  if(user == null && manager == null) {
    return (
      <>
        {helmet()}
        <UserContext.Provider value={user}>
          <RouterProvider router={DefaultRouter} />
        </UserContext.Provider>
      </>
    );
  }
  if(manager != null) {
    return (
      <>
        {helmet()}
        <ManagerContext.Provider value={manager}>
          <RouterProvider router={ManagerAuthRouter} />
        </ManagerContext.Provider>
      </>
    );
  }
  return (
    <>
      {helmet()}
      <UserContext.Provider value={user}>
        {/* Shown over whichever client page they land on, so it cannot be walked around. */}
        <ConsentGate />
        <RouterProvider router={AuthRouter} />
      </UserContext.Provider>
    </>
  );
}
export default App;